<template>
  <div id="estimate-rental-price" ref="estimate-rental-price" :class="$style.section">
    <div :class="[$style.card, { [$style.is24]: !isHg }]">
      <img :src="backgroundImage" alt="" :class="$style.backgroundGraphic" loading="lazy" />
      <div :class="['layout-container', $style.container]">
        <div :class="$style.imageWrapper">
          <img
            :src="estimationImage"
            :alt="t('myProperties.landing.priceEstimation.imageAlt')"
            :class="$style.estimationImage"
            loading="lazy"
          />
        </div>

        <div :class="$style.content">
          <div :class="$style.badges">
            <HgBadge :theme="badgeTheme" :class="$style.badge">
              {{ t('myProperties.landing.priceEstimation.badges.renting') }}
            </HgBadge>
          </div>

          <h2 :class="$style.title">{{ t('myProperties.landing.priceEstimation.title') }}</h2>

          <p :class="$style.description">
            {{ t('myProperties.landing.priceEstimation.description') }}
          </p>

          <div :class="$style.ctaWrapper">
            <HgButton size="large" theme="secondary" :class="$style.cta" @click="goToOverview">
              {{ t('myProperties.landing.priceEstimation.cta') }}
            </HgButton>
            <span :class="$style.ctaSubtext">{{
              t('myProperties.landing.priceEstimation.ctaHelper')
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isPlatformHg } from '@/helpers/Platform/Platform';
import { getPropertyLandingImageUrl } from '@/helpers/Url/Url';
import { OVERVIEW_PAGE_PATH_SOURCE_LANDING } from '@/router/routes/constants';
import { trackingClickEvent } from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { HgBadge, HgButton, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed, onMounted, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const isHg = isPlatformHg();
const { locale, t } = useI18n();
const router = useRouter();
const badgeTheme = computed(() => (isHg ? 'secondary' : 'info'));
const priceEstimationBackgroundHg = getPropertyLandingImageUrl(
  'price-estimation/price_estimation_background_hg_v3.png',
);
const priceEstimationBackgroundIs24 = getPropertyLandingImageUrl(
  'price-estimation/price_estimation_background_is24_v2.png',
);
const priceEstimationImagesHg: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl(
    'price-estimation/price_estimation_hg_de.png',
  ),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl(
    'price-estimation/price_estimation_hg_en.png',
  ),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl(
    'price-estimation/price_estimation_hg_fr.png',
  ),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl(
    'price-estimation/price_estimation_hg_it.png',
  ),
};
const priceEstimationImagesIs24: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl(
    'price-estimation/price_estimation_is24_de.png',
  ),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl(
    'price-estimation/price_estimation_is24_en.png',
  ),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl(
    'price-estimation/price_estimation_is24_fr.png',
  ),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl(
    'price-estimation/price_estimation_is24_it.png',
  ),
};

const backgroundImage = computed(() =>
  isHg ? priceEstimationBackgroundHg : priceEstimationBackgroundIs24,
);
const estimationImage = computed(() => {
  const imagesByLocale = isHg ? priceEstimationImagesHg : priceEstimationImagesIs24;
  const currentLocale = locale.value as HgSupportedLanguage;

  return imagesByLocale[currentLocale] ?? imagesByLocale[HgSupportedLanguage.EN];
});

const goToOverview = () => {
  trackingClickEvent(
    TRACKING_ACTION.GO_TO_MY_REAL_ESTATE,
    TRACKING_LABEL.PROPERTY_RENTAL_PRICE_TEASER,
  );

  router.push(OVERVIEW_PAGE_PATH_SOURCE_LANDING[locale.value]);
};

const componentRef = useTemplateRef('estimate-rental-price');
const route = useRoute();
onMounted(() => {
  if (route.hash === '#estimate-rental-price') {
    componentRef.value?.scrollIntoView({ behavior: 'smooth' });
  }
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.section {
  box-sizing: border-box;
  max-width: $layout-inner-max-width;
  margin: $space-500 auto $space-600;
}

.card {
  background-color: $color-violet-0;
  position: relative;
  overflow: hidden;
  padding: $space-300 0 $space-100 0;
  border-radius: $border-radius-xl;

  &.is24 {
    background-color: $color-grey-30;

    .backgroundGraphic {
      left: 0;
      height: 100%;
      transform: none;
    }

    .estimationImage {
      max-width: 520px;
    }
  }

  @include grid-media-query(md) {
    padding: $space-300 0 $space-100 0;
  }
}

.backgroundGraphic {
  position: absolute;
  top: 0;
  left: $space-200;
  height: 200%;
  width: auto;
  object-fit: contain;
  object-position: left;
  z-index: 0;
  pointer-events: none;
  transform: translate(-50%, -20%);
}

.container {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: $space-200;

  @include grid-media-query(sm) {
    flex-direction: row;
    align-items: center;
    gap: $space-200;
  }
}

.imageWrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.estimationImage {
  width: 100%;
  max-width: 480px;
  height: auto;
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $space-100;
}

.badges {
  display: flex;
  gap: $space-100;
}

.badge {
  background-color: #eef4ff;
  color: #1976d2;
}

.title {
  @include heading-1;
  color: $color-black;
  text-wrap: balance;
  margin: 0;
  hyphens: none;
}

.description {
  @include body-2;
  color: $color-black;
  max-width: 500px;
  margin-top: 0;
}

.ctaWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-50;
  width: 100%;

  @include grid-media-query(sm) {
    width: auto;
  }
}

.cta {
  width: 100%;
  color: $color-blue-60;

  @include grid-media-query(sm) {
    min-width: 240px;
  }
}

.ctaSubtext {
  @include body-3;
  color: gray;
  text-align: center;

  @include grid-media-query(sm) {
    text-align: left;
  }
}
</style>
